import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  DEFAULT_CONTENT_LOCALE,
  type Lesson,
  type TranslationReplacementAction,
} from '@course-authoring/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { lessonKeys, useLessonQuery } from '../../api/courseApi';
import { useLessonBlockCustomizationsQuery } from '../../api/lessonBlockApi';
import {
  useLessonBlockTranslationMutation,
  useLessonBlockTranslationsQuery,
} from '../../api/lessonBlockTranslationApi';
import {
  PublishActionCluster,
  PublishEditingBanner,
  PublishLockBanner,
  type PublishClusterActions,
} from '../../components';
import { LessonDeleteAction } from '../../components/EditorDeleteAction';
import {
  useLessonClientScope,
  useSeedLessonContent,
} from '../../hooks';
import { ResolvedAccountConfigProvider } from '../../config/ResolvedAccountConfigProvider';
import { useClientContext } from '../../hooks/useClientContext';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { getRememberedListPath } from '../../hooks/useListPathMemory';
import { selectContentState, useEditorStore } from '../../store/editorStore';
import { COPY_CONFIRMATION_DURATION_MS } from '../../utils/copyConfirmation';
import { formatLessonHumanId } from '../../utils/humanIds';
import { LessonEditorEdit } from './LessonEditorEdit';
import {
  LessonEditorMetadata,
  type LessonMetadataSaveState,
} from './LessonEditorMetadata';
import { LessonEditorPreview } from './LessonEditorPreview';
import { lessonEditorPageStyles as styles } from './lessonEditorPageStyles';
import { useLessonBlockTranslationAutosave } from './useLessonBlockTranslationAutosave';
import { useEnglishBlockTranslationDecisions } from './useEnglishBlockTranslationDecisions';
import { useLessonBlockAutosave } from './useLessonBlockAutosave';

type EditorView = 'edit' | 'metadata' | 'preview';

interface PendingLessonNavigationState {
  pendingLessonMeta?: Partial<Pick<Lesson, 'description' | 'estimatedTimeMinutes'>>;
  /** NA-437: set when this lesson was opened via "Copy to another lesson" → "Open that lesson after copying". */
  highlightBlockId?: string;
}

const IDLE_METADATA_SAVE_STATE: LessonMetadataSaveState = {
  status: 'idle',
  isPending: false,
};

export function LessonEditorPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as PendingLessonNavigationState | null;
  const { selectedLocale, setSelectedLocale, selectedLanguageLabel } = useLanguageContext();
  const setClientContextActive = useEditorStore((state) => state.setClientContextActive);
  const {
    clientAccountId,
    isClientContext,
    metadataAccountId,
    hydrationScopeKey,
  } = useLessonClientScope(lessonId, selectedLocale);
  const { clientContext: activeClientAccount } = useClientContext();
  const queryClient = useQueryClient();
  const clusterActionsRef = useRef<PublishClusterActions | null>(null);
  const metadataFlushRef = useRef<(() => Promise<void>) | null>(null);
  const [metadataSaveState, setMetadataSaveState] = useState(IDLE_METADATA_SAVE_STATE);
  const isPreviewMode = useEditorStore((state) => state.isPreviewMode);
  const setIsPreviewMode = useEditorStore((state) => state.setIsPreviewMode);
  const setContent = useEditorStore((state) => state.setContent);
  const lessonMeta = useEditorStore((state) => state.lessonMeta);
  const updateLessonMeta = useEditorStore((state) => state.updateLessonMeta);
  const resetPreviewProgress = useEditorStore((state) => state.resetPreviewProgress);
  const setPreviewCurrentLocation = useEditorStore((state) => state.setPreviewCurrentLocation);

  const searchParams = new URLSearchParams(location.search);
  const urlMode = searchParams.get('mode');
  const shouldShowWelcome = searchParams.get('welcomeModal') === 'true';
  const shouldForcePrivacy = searchParams.get('showPrivacy') === '1';
  // Preview-only sandbox flag — mirrors the equivalent on CourseLearnerPreviewPage.
  const shouldShowSaveExitBar = searchParams.get('saveExitBar') === 'true';
  const isMetadataMode = urlMode === 'metadata';
  const isPreviewRoute = urlMode === 'preview';
  const currentEditorView: EditorView = isMetadataMode
    ? 'metadata'
    : (isPreviewRoute || isPreviewMode)
      ? 'preview'
      : 'edit';
  const lessonReadAccountId = currentEditorView === 'preview'
    ? metadataAccountId
    : clientAccountId;
  const lessonReadScopeKey = currentEditorView === 'preview'
    ? `${lessonId ?? ''}:${selectedLocale}:${lessonReadAccountId ?? 'base'}`
    : hydrationScopeKey;
  const isBlockTranslationMode = currentEditorView === 'edit'
    && selectedLocale !== DEFAULT_CONTENT_LOCALE;

  const prevPreviewModeRef = useRef(isPreviewMode);
  const isNavigatingRef = useRef(false);
  const pendingMetadataAppliedRef = useRef<string | null>(null);
  const pendingHighlightAppliedRef = useRef<string | null>(null);
  const [highlightBlockId, setHighlightBlockId] = useState<string | null>(null);

  // NA-437: applies once per (lessonId, highlightBlockId) pair — same
  // ref-guard shape as pendingMetadataAppliedRef above — then clears itself
  // after COPY_CONFIRMATION_DURATION_MS so the ring is transient.
  useEffect(() => {
    const pendingBlockId = navigationState?.highlightBlockId;
    if (!pendingBlockId) return;
    const pendingKey = `${lessonId ?? ''}:${pendingBlockId}`;
    if (pendingHighlightAppliedRef.current === pendingKey) return;
    pendingHighlightAppliedRef.current = pendingKey;
    setHighlightBlockId(pendingBlockId);
    const timeout = window.setTimeout(() => setHighlightBlockId(null), COPY_CONFIRMATION_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [lessonId, navigationState?.highlightBlockId]);

  useEffect(() => {
    if (isPreviewRoute && (shouldShowWelcome || shouldForcePrivacy)) return;
    if (prevPreviewModeRef.current === isPreviewMode) return;
    prevPreviewModeRef.current = isPreviewMode;

    if (isPreviewRoute === isPreviewMode) return;
    if (!isPreviewMode && isMetadataMode) return;

    isNavigatingRef.current = true;
    if (isPreviewMode) {
      navigate({ search: shouldShowWelcome ? '?mode=preview&welcomeModal=true' : '?mode=preview' });
    } else {
      navigate({ search: '' }, { replace: true });
    }
  }, [isPreviewMode, navigate, isPreviewRoute, isMetadataMode, shouldShowWelcome, shouldForcePrivacy]);

  useEffect(() => {
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    if (isPreviewRoute !== isPreviewMode) {
      if (isPreviewRoute) {
        setPreviewCurrentLocation(lessonId ?? lessonMeta.id);
        setIsPreviewMode(true);
      } else {
        resetPreviewProgress();
        setIsPreviewMode(false);
      }
      prevPreviewModeRef.current = isPreviewRoute;
    }
  }, [
    isPreviewRoute,
    isPreviewMode,
    lessonId,
    lessonMeta.id,
    setIsPreviewMode,
    setPreviewCurrentLocation,
    resetPreviewProgress,
  ]);

  const { data: lessonQueryData, isLoading, isFetching: isLessonQueryFetching, error } = useLessonQuery(lessonId!, {
    locale: selectedLocale,
    accountId: lessonReadAccountId,
  });

  // publishState and deleteEligibility are server-owned, not undoable editor content.
  const publishState = lessonQueryData?.publishState ?? null;
  const deleteEligibility = lessonQueryData?.deleteEligibility ?? undefined;
  const lessonData = useMemo(() => {
    if (!lessonQueryData) return undefined;
    return {
      lessonMeta: lessonQueryData.lessonMeta,
      blocks: lessonQueryData.blocks,
      blockOrder: lessonQueryData.blockOrder,
    };
  }, [lessonQueryData]);
  const lessonPreviewData = useMemo(() => {
    if (!lessonData) return undefined;
    return {
      ...lessonData,
      ...(lessonQueryData?.narration ? { narration: lessonQueryData.narration } : {}),
    };
  }, [lessonData, lessonQueryData?.narration]);
  const isContentEditorLocked = publishState !== null && publishState.editable === false;

  // Standard context + English only — see block-customization-visibility's
  // "two independent conditions" requirement (NA-295). Gated here, once, so
  // downstream components never need to re-derive it from client-context/
  // locale flags themselves.
  const blockCustomizationsQuery = useLessonBlockCustomizationsQuery(
    lessonId!,
    currentEditorView === 'edit' && !isClientContext && selectedLocale === DEFAULT_CONTENT_LOCALE,
  );
  const showBlockCustomizations = !isClientContext && selectedLocale === DEFAULT_CONTENT_LOCALE;

  const blockTranslationQuery = useLessonBlockTranslationsQuery(
    lessonId!,
    selectedLocale,
    clientAccountId,
    currentEditorView === 'edit',
  );
  const blockTranslationMutation = useLessonBlockTranslationMutation(
    lessonId!,
    selectedLocale,
    clientAccountId,
  );
  const mutateBlockTranslation = blockTranslationMutation.mutateAsync;
  const saveTranslatedBlock = useCallback((blockId: string, fields: Record<string, unknown>) =>
    mutateBlockTranslation({ blockId, patch: { fields } }),
  [mutateBlockTranslation]);
  const reviseBlockTranslationDecision = useCallback((
    blockId: string,
    action: TranslationReplacementAction,
  ) => mutateBlockTranslation({ blockId, patch: { replacementAction: action } }),
  [mutateBlockTranslation]);
  const refetchBlockTranslations = blockTranslationQuery.refetch;
  const refetchBlockTranslationProjection = useCallback(async () => {
    const result = await refetchBlockTranslations();
    return result?.data;
  }, [refetchBlockTranslations]);
  const blockTranslationAutosave = useLessonBlockTranslationAutosave({
    enabled: isBlockTranslationMode && !isContentEditorLocked,
    scopeKey: hydrationScopeKey,
    projection: isBlockTranslationMode ? blockTranslationQuery.data : undefined,
    save: saveTranslatedBlock,
  });
  const englishBlockTranslationDecisions = useEnglishBlockTranslationDecisions({
    enabled: currentEditorView === 'edit'
      && selectedLocale === DEFAULT_CONTENT_LOCALE,
    scopeKey: hydrationScopeKey,
    projection: blockTranslationQuery.data,
    reviseDecision: reviseBlockTranslationDecision,
    refetchProjection: refetchBlockTranslationProjection,
  });

  useEffect(() => {
    setClientContextActive(isClientContext);
    return () => setClientContextActive(false);
  }, [isClientContext, setClientContextActive]);

  const getCurrentContent = useCallback(
    () => selectContentState(useEditorStore.getState()),
    [],
  );

  const granularAutosave = useLessonBlockAutosave({
    enabled: Boolean(lessonData)
      && currentEditorView === 'edit'
      && selectedLocale === DEFAULT_CONTENT_LOCALE
      && !isContentEditorLocked,
    scopeKey: `${lessonId ?? ''}:en:${clientAccountId ?? 'base'}`,
    lessonId: lessonId!,
    accountId: clientAccountId,
    getSnapshot: getCurrentContent,
    getReplacementActions: englishBlockTranslationDecisions.getReplacementActions,
    onBlockContentSaved: englishBlockTranslationDecisions.onBlockContentSaved,
  });

  const lastSavedContentRef = granularAutosave.lastSavedSnapshotRef;
  const pendingSaveContentRef = granularAutosave.pendingSaveSnapshotRef;

  const registerMetadataFlush = useCallback((flush: (() => Promise<void>) | null) => {
    metadataFlushRef.current = flush;
  }, []);

  const alignGeneralAutosaveBaseline = useCallback(() => {
    granularAutosave.alignBaseline();
  }, [granularAutosave.alignBaseline]);

  const flushActiveSave = useCallback(async () => {
    if (currentEditorView === 'metadata') {
      await metadataFlushRef.current?.();
      alignGeneralAutosaveBaseline();
      return;
    }
    if (currentEditorView === 'edit') {
      if (isBlockTranslationMode) {
        await blockTranslationAutosave.flush();
      } else {
        await granularAutosave.flush();
      }
    }
  }, [
    alignGeneralAutosaveBaseline,
    blockTranslationAutosave.flush,
    currentEditorView,
    granularAutosave.flush,
    isBlockTranslationMode,
  ]);

  const handleLocaleChange = useCallback(async (locale: string) => {
    try {
      await flushActiveSave();
    } catch {
      return;
    }
    resetPreviewProgress();
    setSelectedLocale(locale);
  }, [flushActiveSave, resetPreviewProgress, setSelectedLocale]);

  const handleBackToLessons = useCallback(async () => {
    try {
      await flushActiveSave();
    } catch {
      return;
    }
    navigate(getRememberedListPath('/lessons'));
  }, [flushActiveSave, navigate]);

  useSeedLessonContent({
    lessonId,
    lessonData,
    hydrationKey: lessonReadScopeKey,
    expectedLocale: selectedLocale,
    setContent,
    lastSavedContentRef,
    pendingSaveContentRef,
    getCurrentContent,
    isFetching: isLessonQueryFetching,
  });

  useLayoutEffect(() => {
    granularAutosave.alignBaseline();
  }, [clientAccountId, granularAutosave.alignBaseline, lessonId, selectedLocale]);

  useEffect(() => {
    if (!lessonData || !navigationState?.pendingLessonMeta) return;

    const pendingKey = `${lessonId ?? ''}:${JSON.stringify(navigationState.pendingLessonMeta)}`;
    if (pendingMetadataAppliedRef.current === pendingKey) return;

    const updates: Partial<Pick<Lesson, 'description' | 'estimatedTimeMinutes'>> = {};
    if (
      typeof navigationState.pendingLessonMeta.description === 'string'
      && navigationState.pendingLessonMeta.description !== lessonMeta.description
    ) {
      updates.description = navigationState.pendingLessonMeta.description;
    }
    if (
      typeof navigationState.pendingLessonMeta.estimatedTimeMinutes === 'number'
      && navigationState.pendingLessonMeta.estimatedTimeMinutes !== lessonMeta.estimatedTimeMinutes
    ) {
      updates.estimatedTimeMinutes = navigationState.pendingLessonMeta.estimatedTimeMinutes;
    }
    if (Object.keys(updates).length > 0) updateLessonMeta(updates);
    pendingMetadataAppliedRef.current = pendingKey;
  }, [
    lessonData,
    lessonId,
    lessonMeta.description,
    lessonMeta.estimatedTimeMinutes,
    navigationState?.pendingLessonMeta,
    updateLessonMeta,
  ]);

  const handleLessonTitleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    updateLessonMeta({ title: event.target.value });
  }, [updateLessonMeta]);

  const handleSetEditorView = useCallback(async (view: EditorView, previewFromBlockId?: string) => {
    if (currentEditorView === view) return;
    try {
      await flushActiveSave();
    } catch {
      return;
    }

    if (view === 'preview') {
      if (previewFromBlockId) {
        // Preview-from-block targeting happens only after the flush succeeded,
        // so an aborted transition leaves no preview progress behind.
        const {
          blockOrder,
          markPreviewBlockComplete,
          setPreviewTargetBlockId,
          setReturnTargetBlockId,
        } = useEditorStore.getState();
        const targetIndex = blockOrder.indexOf(previewFromBlockId);
        for (let i = 0; i < targetIndex; i++) {
          const precedingBlockId = blockOrder[i];
          if (precedingBlockId) markPreviewBlockComplete(precedingBlockId);
        }
        setReturnTargetBlockId(previewFromBlockId);
        setPreviewTargetBlockId(previewFromBlockId);
      }
      if (selectedLocale !== DEFAULT_CONTENT_LOCALE) {
        await queryClient.invalidateQueries({
          queryKey: lessonKeys.detail(lessonId!, selectedLocale, metadataAccountId),
          exact: true,
        });
      }
      isNavigatingRef.current = true;
      navigate({ search: shouldShowWelcome ? '?mode=preview&welcomeModal=true' : '?mode=preview' });
      setPreviewCurrentLocation(lessonMeta.id);
      setIsPreviewMode(true);
      return;
    }

    if (isPreviewMode) {
      resetPreviewProgress();
      setIsPreviewMode(false);
    }

    isNavigatingRef.current = true;
    navigate(
      { search: view === 'metadata' ? '?mode=metadata' : '' },
      view === 'edit' ? { replace: true } : undefined,
    );
  }, [
    currentEditorView,
    flushActiveSave,
    isBlockTranslationMode,
    isPreviewMode,
    lessonId,
    lessonMeta.id,
    metadataAccountId,
    navigate,
    queryClient,
    resetPreviewProgress,
    selectedLocale,
    setIsPreviewMode,
    setPreviewCurrentLocation,
    shouldShowWelcome,
  ]);

  const persistDraftBeforeDeploy = useCallback(() => flushActiveSave(), [flushActiveSave]);

  const handlePublishStateChange = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['lessons'] });
    void queryClient.invalidateQueries({ queryKey: ['courseRecords'] });
  }, [queryClient]);

  const isEditLocked = isContentEditorLocked;
  const isTranslationMetadataView = currentEditorView === 'metadata'
    && selectedLocale !== DEFAULT_CONTENT_LOCALE;
  const showMetadataButton = currentEditorView !== 'preview';
  const activeSaveState = isMetadataMode ? metadataSaveState : {
    status: isBlockTranslationMode ? blockTranslationAutosave.status : granularAutosave.status,
    isPending: isBlockTranslationMode
      ? blockTranslationMutation.isPending
      : granularAutosave.status === 'saving',
  };
  const activeSaveError = isBlockTranslationMode
    ? blockTranslationAutosave.error
      ? `Translation save failed: ${blockTranslationAutosave.error.message}`
      : null
    : granularAutosave.error
      ? `Lesson save failed: ${granularAutosave.error.message}`
      : null;

  if (isLoading) return <div style={styles.loading}>Loading lesson...</div>;
  if (error) return <div style={styles.error}>Error loading lesson: {(error as Error).message}</div>;
  if (isBlockTranslationMode && blockTranslationQuery.isLoading) {
    return <div style={styles.loading}>Loading block translations...</div>;
  }
  if (isBlockTranslationMode && blockTranslationQuery.error) {
    return <div style={styles.error}>Error loading block translations: {(blockTranslationQuery.error as { message?: string }).message}</div>;
  }

  return (
    <div style={styles.editorContainer}>
      <div style={styles.toolbar}>
        <button
          type="button"
          style={styles.backButton}
          onClick={() => void handleBackToLessons()}
        >
          &larr; Lessons
        </button>
        <input
          type="text"
          value={lessonMeta.title}
          onChange={handleLessonTitleChange}
          placeholder="Untitled Lesson"
          style={styles.courseNameInput}
          disabled={currentEditorView !== 'edit' || isEditLocked || isBlockTranslationMode}
        />

        {formatLessonHumanId(lessonMeta.numericId) ? (
          <span style={styles.lessonIdBadge}>{formatLessonHumanId(lessonMeta.numericId)}</span>
        ) : null}
        <div style={styles.modeToggle}>
          {showMetadataButton ? (
            <button
              type="button"
              onClick={() => void handleSetEditorView('metadata')}
              style={{
                ...styles.modeButton,
                ...styles.modeButtonLeft,
                ...(currentEditorView === 'metadata' ? styles.modeButtonActive : {}),
              }}
            >
              📝 Metadata
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => void handleSetEditorView('edit')}
            style={{
              ...styles.modeButton,
              ...(showMetadataButton ? styles.modeButtonMiddle : styles.modeButtonLeft),
              ...(currentEditorView === 'edit' ? styles.modeButtonActive : {}),
            }}
          >
            ✏️ Edit
          </button>
          <button
            type="button"
            onClick={() => void handleSetEditorView('preview')}
            style={{
              ...styles.modeButton,
              ...styles.modeButtonRight,
              ...(currentEditorView === 'preview' ? styles.modeButtonActive : {}),
            }}
          >
            👁️ Preview
          </button>
        </div>

        {!isTranslationMetadataView ? (
          <PublishActionCluster
            contentKind="lesson"
            packageId={lessonId!}
            numericId={lessonMeta.numericId}
            title={lessonMeta.title || 'Untitled Lesson'}
            publishState={publishState}
            clientContext={lessonReadAccountId !== null ? {
              accountId: lessonReadAccountId,
              accountName: activeClientAccount?.name ?? '',
              ...(activeClientAccount?.subdomain ? { accountSlug: activeClientAccount.subdomain } : {}),
            } : null}
            onBeforeAction={persistDraftBeforeDeploy}
            onPublishStateChange={handlePublishStateChange}
            onRegisterActions={(actions) => { clusterActionsRef.current = actions; }}
          />
        ) : null}

        {/* Client context is the only case that withholds Delete (the spec's
            single exclusion); a translation view is still standard context, so
            it keeps the action. */}
        {lessonId && (
          <LessonDeleteAction
            contentId={lessonId}
            title={lessonMeta.title || 'Untitled Lesson'}
            eligibility={deleteEligibility}
            isStandardContext={activeClientAccount === null}
          />
        )}

        <span style={styles.saveStatus}>
          {activeSaveState.status === 'saving' || activeSaveState.isPending
            ? 'Saving...'
            : activeSaveState.status === 'saved'
              ? '✓ Saved'
              : activeSaveState.status === 'error'
                ? '⚠ Save failed'
                : ''}
        </span>
      </div>

      {publishState !== null
        && publishState.state !== 'draft'
        && currentEditorView !== 'preview'
        && !isTranslationMetadataView
        && (
          <div style={styles.publishBannerContainer}>
            {publishState.state === 'locked' ? (
              <PublishLockBanner
                contentKind="lesson"
                lockedByEmtrainEditing={publishState.lockedByEmtrainEditing}
                lastPublishedAt={publishState.lastPublishedAt}
                onUnlock={publishState.lockedByEmtrainEditing
                  ? undefined
                  : () => clusterActionsRef.current?.openUnlock()}
                packageId={!isClientContext || activeClientAccount?.subdomain ? lessonId : undefined}
                accountSlug={isClientContext ? activeClientAccount?.subdomain : undefined}
              />
            ) : (
              <PublishEditingBanner
                onPublishUpdate={() => clusterActionsRef.current?.openPublishUpdate()}
                onCancelEdit={() => clusterActionsRef.current?.cancelEdit()}
                canCancelEdit={publishState.canCancelEdit}
              />
            )}
          </div>
        )}

      {activeSaveError ? (
        <div role="alert" style={styles.saveErrorAlert}>
          {activeSaveError}
        </div>
      ) : null}

      {currentEditorView === 'metadata' ? (
        <LessonEditorMetadata
          lessonId={lessonId!}
          lessonMeta={lessonMeta}
          selectedLocale={selectedLocale}
          selectedLanguageLabel={selectedLanguageLabel}
          metadataAccountId={metadataAccountId}
          onRegisterFlush={registerMetadataFlush}
          onSaveStateChange={setMetadataSaveState}
        />
      ) : currentEditorView === 'preview' ? (
        <LessonEditorPreview
          lessonId={lessonId!}
          lessonData={lessonPreviewData}
          lessonMeta={lessonMeta}
          selectedLocale={selectedLocale}
          onLanguageSelect={handleLocaleChange}
          shouldShowWelcome={shouldShowWelcome}
          shouldForcePrivacy={shouldForcePrivacy}
          shouldShowSaveExitBar={shouldShowSaveExitBar}
        />
      ) : (
        <ResolvedAccountConfigProvider>
        <LessonEditorEdit
          isLocked={isEditLocked}
          lessonId={lessonId}
          highlightBlockId={highlightBlockId}
          onNavigateToLesson={(destinationLessonId, options) => navigate(
            `/lessons/${destinationLessonId}`,
            options?.highlightBlockId ? { state: { highlightBlockId: options.highlightBlockId } } : undefined,
          )}
          blockCustomizations={
            showBlockCustomizations
              ? blockCustomizationsQuery.data
              : undefined
          }
          blockCustomizationsLoading={showBlockCustomizations && blockCustomizationsQuery.isLoading}
          blockCustomizationsError={showBlockCustomizations && blockCustomizationsQuery.isError}
          onRetryBlockCustomizations={() => { void blockCustomizationsQuery.refetch(); }}
          translation={isBlockTranslationMode && blockTranslationQuery.data ? {
            language: selectedLanguageLabel,
            records: blockTranslationQuery.data.blocks,
            drafts: blockTranslationAutosave.drafts,
            onChange: blockTranslationAutosave.updateBlockFields,
            // Translated-block Preview must flush translation drafts and
            // refresh the localized lesson before entering preview — the
            // English direct path renders from the in-memory store and skips
            // both, which would show stale translations here.
            onPreviewFromBlock: (blockId: string) => { void handleSetEditorView('preview', blockId); },
          } : undefined}
          englishTranslation={selectedLocale === DEFAULT_CONTENT_LOCALE ? {
              records: blockTranslationQuery.data?.blocks ?? [],
              decisions: englishBlockTranslationDecisions.decisions,
              onDecision: englishBlockTranslationDecisions.onDecision,
              onSourceChange: englishBlockTranslationDecisions.onSourceChange,
            } : undefined}
        />
        </ResolvedAccountConfigProvider>
      )}
    </div>
  );
}
